// src/pages/RealEstateDetails.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db, firebaseAppId } from '../../../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faArrowLeft,
  faPhone,
  faEnvelope,
  faExpand,
  faCar,
  faFileAlt,
  faBuilding,
  faHome,
  faRoad,
  faMoneyBill,
  faChevronLeft,
  faChevronRight,
  faPlayCircle,
  faPause,               // ← added
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp as faWhatsappBrand } from '@fortawesome/free-brands-svg-icons';
import {
  LoadingMessage,
  ErrorMessage,
  Spinner,
  NoPostsMessage,
} from '../styles/home.styles';
import {
  PageContainer,
  BackButton,
  DetailsCard,
  GallerySection,
  MainImage,
  MainImageItem,
  ExpandButton,
  ThumbnailGallery,
  DetailsSection,
  MainDetails,
  PropertyHeader,
  Title,
  Address,
  Price,
  PropertyFeatures,
  Feature,
  FeatureIcon,
  FeatureLabel,
  FeatureValue,
  Description,
  Sidebar,
  FormTitle,
  ContactOptions,
  WhatsAppButton,
  CallButton,
  EmailButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalImage,
  SectionTitle,
} from '../styles/Realestate_details.styles';
import styled from 'styled-components';

// ── Styled Components ─────────────────────────────────────
const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PrevButton = styled(NavButton)` left: 10px; `;
const NextButton = styled(NavButton)` right: 10px; `;

const ModalNavButton = styled(NavButton)`
  width: 50px;
  height: 50px;
  font-size: 1.5rem;
  background: rgba(0, 0, 0, 0.7);
  &:hover { background: rgba(0, 0, 0, 0.9); }
`;
const ModalPrevButton = styled(ModalNavButton)` left: 20px; `;
const ModalNextButton = styled(ModalNavButton)` right: 20px; `;

// Video player (main view & modal)
const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Play/pause overlay (main view)
const VideoPlayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0,0,0,0.6);
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
  z-index: 5;
`;

// Video URL display (unchanged)
const VideoUrlDisplay = styled.div`
  margin-top: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.85rem;
  word-break: break-all;
  color: #495057;
  white-space: pre-wrap;
`;

// Thumbnail (unchanged)
const Thumbnail = styled.div<{ $bgImage?: string }>`
  width: 80px;
  min-width: 80px;
  height: 60px;
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  background-image: ${props => props.$bgImage ? `url(${props.$bgImage})` : 'none'};
  background-color: #000;
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #007bff;
  }
  &.active {
    border-color: #6c63ff;
    box-shadow: 0 0 0 2px #6c63ff;
  }

  svg {
    font-size: 1.2rem;
    color: white;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
    z-index: 2;
    position: relative;
  }
`;

// ── Interface ─────────────────────────────────────
export interface RealEstateListing {
  id: string;
  category: string;
  status: 'available' | 'sold' | 'pending';
  price?: number;
  name?: string;
  description?: string;
  imageUrls: string[];
  videoUrls: string[];
  latitude?: number;
  longitude?: number;
  villageName?: string;
  logbookNames?: string;
  nin?: string;
  telephone?: string;
  telephone1?: string;
  telephone2?: string;
  whatsapp?: string;
  email?: string;
  logbookFileUrl?: string | null;
  make?: string;
  model?: string;
  bodyType?: string;
  fuel?: string;
  transmission?: string;
  steering?: string;
  chassisNumber?: string;
  yearManufacture?: number;
  yearRegistration?: number;
  landTitleOwnerNames?: string;
  landSize?: string;
  landTitleOwnerNin?: string;
  landTitleOwnerTelephone?: string;
  landTitleFileUrl?: string | null;
  buildingType?: string;
  noOfFloors?: number;
  noOfUnits?: number;
  roadStreetName?: string;
  plotNumber?: number;
  unitDescription?: string;
  propertyType?: string;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

// ── Robust video detection (same as RealEstateCard) ─────────────────────────────────────
const isVideo = (rawUrl: string) => {
  try {
    const decoded = decodeURIComponent(rawUrl);
    const url = new URL(decoded);
    return /\.(mp4|webm|ogg|mov|avi)$/i.test(url.pathname);
  } catch {
    return /\.(mp4|webm|ogg|mov|avi)$/i.test(rawUrl);
  }
};

export const RealEstateDetailsPage: React.FC = () => {
  const { category, id } = useParams<{ category: string; id: string }>();
  const navigate = useNavigate();

  // ── State ─────────────────────────────────────
  const [listing, setListing] = useState<RealEstateListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [formData] = useState({
    name: '',
    email: '',
    phone: '',
    message: `I'm interested in this property and would like more information.`,
  });

  // video control
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // ── Media (images + videos) ─────────────────────────────────────
  const media = useMemo(() => {
    const images = Array.isArray(listing?.imageUrls) ? listing!.imageUrls : [];
    const videos = Array.isArray(listing?.videoUrls) ? listing!.videoUrls : [];
    return [...images, ...videos];
  }, [listing?.imageUrls, listing?.videoUrls]);

  const mediaCount = media.length;

  // ── Navigation helpers ─────────────────────────────────────
  const setIndexAndUpdateUrl = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      const url = media[index];
      setActiveVideoUrl(isVideo(url) ? url : null);
    },
    [media]
  );

  const goToPrev = useCallback(() => {
    const newIndex =
      selectedIndex === 0 ? mediaCount - 1 : selectedIndex - 1;
    setIndexAndUpdateUrl(newIndex);
  }, [mediaCount, selectedIndex, setIndexAndUpdateUrl]);

  const goToNext = useCallback(() => {
    const newIndex =
      selectedIndex === mediaCount - 1 ? 0 : selectedIndex + 1;
    setIndexAndUpdateUrl(newIndex);
  }, [mediaCount, selectedIndex, setIndexAndUpdateUrl]);

  // keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!showModal && mediaCount > 1) {
        if (e.key === 'ArrowLeft') goToPrev();
        if (e.key === 'ArrowRight') goToNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goToPrev, goToNext, showModal, mediaCount]);

  // reset index when id changes
  useEffect(() => setIndexAndUpdateUrl(0), [id, setIndexAndUpdateUrl]);

  // reset video state when slide changes
  useEffect(() => {
    setIsVideoPlaying(false);
    setHasUserInteracted(false);
  }, [selectedIndex]);

  // ── Video playback helpers ─────────────────────────────────────
  const toggleVideoPlayback = useCallback(async () => {
    if (!videoRef.current || !isVideo(media[selectedIndex])) return;

    try {
      if (videoRef.current.paused) {
        await videoRef.current.play();
        setIsVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
      setHasUserInteracted(true);
    } catch (err) {
      console.warn('Playback failed:', err);
    }
  }, [media, selectedIndex]);

  const handleVideoCanPlay = useCallback(async () => {
    if (!videoRef.current || hasUserInteracted || !isVideo(media[selectedIndex]))
      return;

    try {
      await videoRef.current.play();
      setIsVideoPlaying(true);
    } catch {
      console.log('Autoplay blocked – waiting for user click');
    }
  }, [media, selectedIndex, hasUserInteracted]);

  // ── Fetch listing ─────────────────────────────────────
  useEffect(() => {
    const fetchListing = async () => {
      if (!id || !category) {
        setError('Missing ID or category.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const collName = category
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .replace(/_{2,}/g, '_');
        const docRef = doc(
          db,
          `artifacts/${firebaseAppId}/${collName}`,
          id
        );
        const snap = await getDoc(docRef);

        if (snap.exists()) {
          const data = snap.data();
          setListing({
            id: snap.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as RealEstateListing);
        } else {
          setError('Listing not found.');
        }
      } catch (err: any) {
        console.error(err);
        setError(`Failed to load: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, category]);

  // ── Contact handlers ─────────────────────────────────────
  const handleWhatsApp = () => {
    const phone =
      listing?.whatsapp ||
      listing?.telephone2 ||
      listing?.telephone1 ||
      listing?.telephone ||
      '+256712345678';
    const msg = `Hi, I'm interested in "${listing?.name || listing?.make}" in ${
      listing?.villageName
    } for UGX ${listing?.price?.toLocaleString()}.`;
    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,
      '_blank'
    );
  };

  const handleCall = () => {
    const phone = listing?.telephone || listing?.telephone1 || '+256712345678';
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = () => {
    const email = listing?.email || 'info@grema.ug';
    const subject = `Inquiry: ${listing?.name || listing?.make}`;
    const body = `Name: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\n\nMessage: ${formData.message}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  // ── Render features ─────────────────────────────────────
  const renderFeatures = () => {
    if (!listing) return null;
    const features: {
      label: string;
      value: any;
      icon?: any;
    }[] = [];

    const cat = listing.category.toLowerCase();
    if (cat.includes('car')) {
      features.push(
        { label: 'Make', value: listing.make, icon: faCar },
        { label: 'Model', value: listing.model },
        { label: 'Year', value: listing.yearManufacture },
        { label: 'Fuel', value: listing.fuel },
        { label: 'Transmission', value: listing.transmission }
      );
    } else if (cat.includes('land')) {
      features.push(
        { label: 'Size', value: listing.landSize, icon: faMapMarkerAlt },
        { label: 'Title Owner', value: listing.landTitleOwnerNames, icon: faFileAlt }
      );
    } else if (cat.includes('house') || cat.includes('building')) {
      features.push(
        { label: 'Type', value: listing.buildingType, icon: faBuilding },
        { label: 'Floors', value: listing.noOfFloors },
        { label: 'Units', value: listing.noOfUnits }
      );
    }

    const valid = features.filter(f => f.value);
    if (!valid.length) return <p>No features listed.</p>;

    return (
      <PropertyFeatures>
        {valid.map((f, i) => (
          <Feature key={i}>
            <FeatureIcon>
              <FontAwesomeIcon icon={f.icon || faHome} />
            </FeatureIcon>
            <FeatureLabel>{f.label}</FeatureLabel>
            <FeatureValue>{f.value}</FeatureValue>
          </Feature>
        ))}
      </PropertyFeatures>
    );
  };

  // ── Early returns ─────────────────────────────────────
  if (loading) return <LoadingMessage><Spinner /> Loading...</LoadingMessage>;
  if (error) return <ErrorMessage>{error}</ErrorMessage>;
  if (!listing) return <NoPostsMessage>Not found.</NoPostsMessage>;

  // ── JSX ─────────────────────────────────────
  return (
    <PageContainer>
      <BackButton onClick={() => navigate(-1)}>
        <FontAwesomeIcon icon={faArrowLeft} /> Home
      </BackButton>

      <DetailsCard>
        <GallerySection>
          <MainImage>
            {mediaCount > 0 ? (
              <>
                {isVideo(media[selectedIndex]) ? (
                  <>
                    <VideoPlayer
                      ref={videoRef}
                      src={media[selectedIndex]}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      onCanPlay={handleVideoCanPlay}
                      onClick={toggleVideoPlayback}
                    />
                    <VideoPlayOverlay onClick={toggleVideoPlayback}>
                      <FontAwesomeIcon
                        icon={isVideoPlaying ? faPause : faPlayCircle}
                        size="2x"
                      />
                    </VideoPlayOverlay>
                  </>
                ) : (
                  <MainImageItem
                    src={media[selectedIndex]}
                    alt={listing.name || listing.make}
                  />
                )}

                {mediaCount > 1 && (
                  <>
                    <PrevButton onClick={goToPrev}>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </PrevButton>
                    <NextButton onClick={goToNext}>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </NextButton>
                  </>
                )}

                <ExpandButton onClick={() => setShowModal(true)}>
                  <FontAwesomeIcon icon={faExpand} />
                </ExpandButton>
              </>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  color: '#777',
                  padding: '40px',
                }}
              >
                <FontAwesomeIcon icon={faPlayCircle} size="3x" />
                <p>No media available</p>
              </div>
            )}
          </MainImage>

         

          {/* Thumbnails */}
          {mediaCount > 1 && (
            <ThumbnailGallery>
              {media.map((url, i) => {
                const isVid = isVideo(url);
                return (
                  <Thumbnail
                    key={i}
                    $bgImage={isVid ? undefined : url}
                    className={selectedIndex === i ? 'active' : ''}
                    onClick={() => setIndexAndUpdateUrl(i)}
                  >
                    {isVid && (
                      <video
                        src={url}
                        muted
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          opacity: 0.8,
                        }}
                      />
                    )}
                    {isVid && <FontAwesomeIcon icon={faPlayCircle} />}
                  </Thumbnail>
                );
              })}
            </ThumbnailGallery>
          )}
        </GallerySection>

        <DetailsSection>
          <MainDetails>
            <PropertyHeader>
              <Title>{listing.name || listing.make || 'Untitled'}</Title>
              <Address>
                <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                {listing.villageName || 'N/A'}
              </Address>
              <Price>
                <FontAwesomeIcon icon={faMoneyBill} />{' '}
                {listing.price
                  ? `UGX ${listing.price.toLocaleString()}`
                  : 'Contact'}
              </Price>
            </PropertyHeader>

            <div>
              <SectionTitle>Features</SectionTitle>
              {renderFeatures()}
            </div>
            <div>
              <SectionTitle>Description</SectionTitle>
              <Description>
                {listing.description || 'No description.'}
              </Description>
            </div>
          </MainDetails>

          <Sidebar>
            <FormTitle>Contact Seller</FormTitle>
            <ContactOptions>
              <WhatsAppButton onClick={handleWhatsApp}>
                <FontAwesomeIcon icon={faWhatsappBrand} size="lg" /> WhatsApp
              </WhatsAppButton>
              <CallButton onClick={handleCall}>
                <FontAwesomeIcon icon={faPhone} /> Call
              </CallButton>
              <EmailButton onClick={handleEmail}>
                <FontAwesomeIcon icon={faEnvelope} /> Email
              </EmailButton>
            </ContactOptions>
          </Sidebar>
        </DetailsSection>
      </DetailsCard>

      {/* ── Full-Screen Modal ── */}
      {showModal && mediaCount > 0 && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{listing.name || listing.make}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            {isVideo(media[selectedIndex]) ? (
              <>
                <VideoPlayer
                  ref={videoRef}
                  src={media[selectedIndex]}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onCanPlay={handleVideoCanPlay}
                  onClick={toggleVideoPlayback}
                  style={{
                    width: '100%',
                    height: '80vh',
                    objectFit: 'contain',
                    background: '#000',
                    cursor: 'pointer',
                  }}
                />
                <VideoPlayOverlay
                  onClick={toggleVideoPlayback}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'auto',
                  }}
                >
                  <FontAwesomeIcon
                    icon={isVideoPlaying ? faPause : faPlayCircle}
                    size="3x"
                  />
                </VideoPlayOverlay>
              </>
            ) : (
              <ModalImage src={media[selectedIndex]} alt="Full view" />
            )}

            {mediaCount > 1 && (
              <>
                <ModalPrevButton onClick={goToPrev}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </ModalPrevButton>
                <ModalNextButton onClick={goToNext}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </ModalNextButton>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};